import { useBoolean } from 'minimal-shared/hooks';
import React, { useRef, useState, useEffect, useCallback } from 'react';

import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';

import { Upload } from './Upload';
import Progress from './Progress';
import useLocales from '../../../hooks/useLocales';
import { fData } from '../../../utils/file/format-number';
import useNotifications from '../../../hooks/useNotifications';
import documentService from '../../../services/documentService';
import { DocumentUpload } from '../../../@types/documents/types';
import { setDriveListIdState } from '../../../redux/slices/dlxDrive';

// ----------------------------------------------------------------------

type DocumentsUploadFormProps = {
  ALLOWED_FILE_TYPES: string;
  MAX_FILE_SIZE: number;
  files: File[];
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  open: boolean;
  setOpen: (open: boolean) => void;
  folderId?: number;
};

const DocumentsUploadDialog: React.FC<DocumentsUploadFormProps> = ({
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE,
  files,
  setFiles,
  open,
  setOpen,
  folderId,
}) => {
  const { t } = useLocales();
  const { enqueueSuccess } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [valueBuffer, setValueBuffer] = useState(0);
  const [uploadingFile, setUploadingFile] = useState('');
  const [itemsRemaining, setItemsRemaining] = useState('');

  const showPreview = useBoolean();

  const handleClose = () => {
    setFiles([]);
    setOpen(false);
  };

  const descriptionElementRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (open) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [open]);

  const handleDropMultiFile = useCallback((acceptedFiles: File[]) => {
    setFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
  }, []);

  const handleRemoveFile = useCallback((inputFile: File | string) => {
    setFiles((prevFiles) => prevFiles.filter((fileFiltered) => fileFiltered !== inputFile));
  }, []);

  const handleRemoveAllFiles = useCallback(() => {
    setFiles([]);
  }, []);

  const handleUploadFiles = async () => {
    const totalSize = files.reduce((acc, file) => acc + (file instanceof File ? file.size : 0), 0);
    let uploadedSize = 0;
    let remainingFiles = files.length;
    let countUploadedFiles = 0;

    setLoading(true);

    for (let i = 0; i < files.length; i++) {
      setUploadingFile(`${files[i].name} (${fData(files[i].size)})`);
      setItemsRemaining(`${remainingFiles} (${fData(totalSize - uploadedSize)})`);

      setValueBuffer((files[i].size / totalSize) * 100);

      try {
        const name = files[i].name.replace(/\.[^/.]+$/, '');
        const extension = files[i].name.split('.').pop() || '';

        const model = {
          name: `${name}.${extension}`,
          extension,
          file: files[i],
          isEditable: false,
        } as unknown as DocumentUpload;

        if (folderId) {
          model.folderId = folderId;
        }

        await documentService.createDocument(model);

        countUploadedFiles += 1;
      } catch (e) {
        console.log(e);
      } finally {
        uploadedSize += files[i].size;
        remainingFiles -= 1;

        setProgress((uploadedSize / totalSize) * 100);
      }
    }

    setDriveListIdState();
    enqueueSuccess(t('dlxDrive.notifications.filesUploaded', { count: countUploadedFiles }));

    setFiles([]);
    setProgress(0);
    setUploadingFile('');
    setItemsRemaining('');
    setLoading(false);
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (loading && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
          return;
        }
        handleClose();
      }}
      scroll="paper"
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading}
      slotProps={{
        paper: {
          sx: {
            height: loading ? 'auto' : '90vh',
            maxHeight: loading ? 'auto' : '90vh',
            display: 'flex',
          },
        },
      }}
    >
      <DialogTitle id="scroll-dialog-title">
        {loading ? `${t('documents.upload.uploadingFiles')}...` : t('documents.upload.uploadFiles')}
      </DialogTitle>

      <DialogContent
        dividers
        sx={[
          loading && {
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'auto',
          },
        ]}
      >
        {!loading && (
          <>
            <FormControlLabel
              label={t('documents.upload.showThumbnails')}
              control={
                <Switch
                  checked={showPreview.value}
                  onClick={showPreview.onToggle}
                  slotProps={{ input: { id: 'show-thumbnails-switch' } }}
                />
              }
              sx={{ mb: 3, width: 1, justifyContent: 'flex-end' }}
            />

            <Upload
              multiple
              thumbnail={showPreview.value}
              value={files}
              accept={ALLOWED_FILE_TYPES}
              maxSize={MAX_FILE_SIZE}
              onDrop={handleDropMultiFile}
              onRemove={handleRemoveFile}
              onRemoveAll={handleRemoveAllFiles}
              onUpload={handleUploadFiles}
            />
          </>
        )}

        {loading && (
          <Progress
            progress={progress}
            valueBuffer={valueBuffer}
            uploadingFile={uploadingFile}
            itemsRemaining={itemsRemaining}
          />
        )}
      </DialogContent>

      {!loading && (
        <DialogActions>
          <Button onClick={handleClose} variant="outlined" color="inherit" id="mz5nnxqa">
            {t('shared.buttons.cancel')}
          </Button>

          <Button
            disabled={files.length === 0}
            onClick={() => setFiles([])}
            variant="outlined"
            color="inherit"
            id="mz5Cpxqa"
          >
            {t('shared.buttons.removeAll')}
          </Button>

          <LoadingButton
            disabled={files.length === 0}
            loading={loading}
            onClick={handleUploadFiles}
            type="button"
            variant="contained"
            id="yd0sown5"
          >
            {t('shared.buttons.upload')}
          </LoadingButton>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default DocumentsUploadDialog;
